import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { postsAPI } from '../../services/api';
import { Card, Button } from '../ui';
import Loading from '../common/Loading';
import { handleApiError, getImageUrl, getMediaTypeFromUrl } from '../../utils/helpers';
import { X, FileText, Image } from 'lucide-react';

const SeoPostPreviewModal = ({ postId, isOpen, onClose }) => {
  const { isRTL } = useLanguage();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && postId) {
      fetchPost();
    } else {
      setPost(null);
      setError(null);
    }
  }, [isOpen, postId]);

  const fetchPost = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await postsAPI.getById(postId);
      const p = res.data.post;
      if (p?.current_image_url && !p.current_image_url.startsWith('http')) {
        p.current_image_url = getImageUrl(p.current_image_url);
      }
      setPost(p);
    } catch (err) {
      setError(handleApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  const isArticle = post?.content_type === 'blog';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" dir={isRTL ? 'rtl' : 'ltr'} onClick={onClose}>
      <Card
        className="w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-4 border-b border-slate-200 shrink-0">
          <div className="flex items-center gap-2">
            {isArticle ? (
              <FileText className="w-5 h-5 text-sky-600" />
            ) : (
              <Image className="w-5 h-5 text-amber-600" />
            )}
            <h3 className="text-lg font-semibold text-slate-900">
              {isArticle ? (isRTL ? 'معاينة المقال' : 'Article preview') : (isRTL ? 'معاينة التصميم' : 'Design preview')}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-slate-700"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6">
          {loading && <Loading />}
          {error && <p className="text-rose-600 text-sm">{error}</p>}
          {post && !loading && (
            <div className="space-y-4">
              <div>
                <p className="text-xs font-medium text-slate-500 mb-1">{isRTL ? 'العنوان' : 'Title'}</p>
                <h4 className="text-lg font-semibold text-slate-900">{post.title || '-'}</h4>
              </div>

              {post.description && (
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-1">{isRTL ? 'الوصف' : 'Description'}</p>
                  <p className="text-sm text-slate-700">{post.description}</p>
                </div>
              )}

              {isArticle && (post.meta_title || post.meta_description) && (
                <div className="space-y-2 p-3 rounded-lg bg-slate-50 border border-slate-200">
                  {post.meta_title && (
                    <div>
                      <p className="text-xs font-medium text-slate-500 mb-0.5">{isRTL ? 'عنوان الميتا' : 'Meta title'}</p>
                      <p className="text-sm text-slate-800">{post.meta_title}</p>
                    </div>
                  )}
                  {post.meta_description && (
                    <div>
                      <p className="text-xs font-medium text-slate-500 mb-0.5">{isRTL ? 'وصف الميتا' : 'Meta description'}</p>
                      <p className="text-sm text-slate-700">{post.meta_description}</p>
                    </div>
                  )}
                </div>
              )}

              {isArticle && post.rich_description && (
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-1">{isRTL ? 'المحتوى' : 'Content'}</p>
                  <div
                    className="prose prose-sm max-w-none text-slate-700 prose-p:my-1 prose-headings:my-2"
                    dangerouslySetInnerHTML={{ __html: post.rich_description }}
                  />
                </div>
              )}

              {!isArticle && post.current_image_url && (
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-2">{isRTL ? 'المعاينة' : 'Preview'}</p>
                  {getMediaTypeFromUrl(post.current_image_url) === 'video' ? (
                    <video
                      src={post.current_image_url}
                      controls
                      className="w-full max-h-80 rounded-lg border border-slate-200 object-contain"
                    />
                  ) : (
                    <img
                      src={post.current_image_url}
                      alt={post.title}
                      className="w-full max-h-96 rounded-lg border border-slate-200 object-contain"
                    />
                  )}
                </div>
              )}

              {!isArticle && post.current_caption && (
                <div>
                  <p className="text-xs font-medium text-slate-500 mb-1">{isRTL ? 'النسخة' : 'Caption'}</p>
                  <p className="text-sm text-slate-700 whitespace-pre-wrap">{post.current_caption}</p>
                </div>
              )}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default SeoPostPreviewModal;
